import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowRight, MessagesSquare, BookOpen, Radio, Sparkles, BookOpenText, ShieldCheck, Sun } from "lucide-react";
import { CrossIcon } from "@/components/CrossIcon";

const HERO = "https://images.unsplash.com/photo-1642022143908-fe7e3160a56e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxiaWJsZSUyMGFuY2llbnQlMjBwYXJjaG1lbnQlMjBsaWdodCUyMG9wZW4lMjBib29rJTIwc3R1ZHl8ZW58MHx8fHwxNzg5OTUyMzA1fDA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { title: "The Father", verse: "one God and Father of all, who is over all, and through all, and in us all.", ref: "Ephesians 4:6" },
  { title: "The Son", verse: "In him was life, and the life was the light of men.", ref: "John 1:4" },
  { title: "The Word", verse: "Every Scripture is God-breathed and profitable for teaching.", ref: "2 Timothy 3:16" },
  { title: "The Gospel", verse: "For by grace you have been saved through faith.", ref: "Ephesians 2:8" },
];

const SPOTLIGHT = [
  { word: "Immanuel", lang: "Hebrew", script: "עִמָּנוּ אֵל", meaning: "\"God with us\" — the promise fulfilled in Christ." },
  { word: "Logos", lang: "Greek", script: "λόγος", meaning: "\"The Word\" — the eternal Son through whom all was made." },
  { word: "Hesed", lang: "Hebrew", script: "חֶסֶד", meaning: "God's steadfast, covenant loving-kindness." },
];

const CARDS = [
  { to: "/fellowship", icon: MessagesSquare, title: "Fellowship Hall", desc: "Ask questions and encourage one another — openly, no account needed.", testid: "home-card-fellowship" },
  { to: "/read", icon: BookOpenText, title: "Read the Bible", desc: "Read any passage in the WEB (World English Bible), from a single verse to a whole chapter, with Bible maps alongside.", testid: "home-card-read" },
  { to: "/word-study", icon: BookOpen, title: "Bible Word Study", desc: "Search original Hebrew, Greek & Aramaic meanings, unpack any parable, and see the places on 1,000+ Bible maps.", testid: "home-card-word-study" },
  { to: "/media", icon: Radio, title: "Gospel Media", desc: "Curated sermons, teaching videos, and Christ-exalting podcasts.", testid: "home-card-media" },
];

export default function Home() {
  const [verse, setVerse] = useState(null);
  useEffect(() => {
    api.get("/verse-of-the-day").then(({ data }) => setVerse(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Verse of the Day */}
      {verse && (
        <div data-testid="verse-of-the-day" className="border-b border-ink/10 bg-ink-deep text-parchment animate-fade-in">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold whitespace-nowrap shrink-0">
              <Sun className="w-3.5 h-3.5" /> Verse of the Day
            </span>
            <p className="font-serif text-lg sm:text-xl italic leading-snug">
              "{verse.text}"
              <span className="not-italic font-mono text-[11px] text-gold ml-2 whitespace-nowrap">— {verse.reference} ({verse.translation})</span>
            </p>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 animate-rise">
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold mb-6">
              <CrossIcon className="w-4 h-4" /> Immanuel — God with us
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-700 leading-[1.05] text-ink">
              Proclaiming the Unchanging Gospel of God the Father & Jesus Christ
            </h1>
            <p className="mt-6 text-base sm:text-lg text-ink/70 leading-relaxed max-w-xl">
              A sanctuary for deep Scripture word study, open fellowship among brothers and sisters,
              and curated gospel audio & video.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/read"
                data-testid="hero-cta-read"
                className="inline-flex items-center gap-2 bg-gold hover:bg-ink text-ink hover:text-parchment font-700 px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                <BookOpenText className="w-4 h-4" /> Read The Bible
              </Link>
              <Link
                to="/fellowship"
                data-testid="hero-cta-fellowship"
                className="inline-flex items-center gap-2 border border-ink/20 hover:border-ink text-ink font-700 px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                Enter Fellowship Hall
              </Link>
              <Link
                to="/word-study"
                data-testid="hero-cta-word-study"
                className="inline-flex items-center gap-2 border border-ink/20 hover:border-ink text-ink font-700 px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                <Sparkles className="w-4 h-4 text-gold" /> Bible Word Study AI
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 animate-fade-in">
            <div className="relative">
              <div className="absolute -inset-4 bg-gold/10 rounded-[2rem] blur-2xl" />
              <img
                src={HERO}
                alt="Open Bible on parchment"
                className="relative rounded-[1.75rem] shadow-2xl w-full object-cover aspect-[4/3] ring-1 ring-ink/10"
              />
              <div className="absolute -bottom-6 -left-4 sm:left-6 bg-ink-deep text-parchment rounded-2xl px-6 py-4 shadow-xl max-w-[15rem]">
                <p className="font-serif text-lg italic leading-snug">"Your word is a lamp to my feet, and a light for my path."</p>
                <p className="font-mono text-[10px] text-gold mt-1">Psalm 119:105</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-serif text-3xl sm:text-4xl font-600 text-ink mb-2">Our Statement of Faith</h2>
        <p className="text-ink/60 max-w-2xl mb-10">The pillars on which this ministry stands, drawn wholly from Scripture.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map((p, i) => (
            <div
              key={p.title}
              className="bg-card rounded-2xl border border-border p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <span className="font-mono text-xs text-gold">0{i + 1}</span>
              <h3 className="font-serif text-2xl font-600 text-ink mt-1 mb-3">{p.title}</h3>
              <p className="text-sm text-ink/70 leading-relaxed">{p.verse}</p>
              <p className="font-mono text-[11px] text-olive mt-3">{p.ref}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Word spotlight */}
      <section className="bg-ink-deep text-parchment py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl font-600">Original Word Spotlight</h2>
              <p className="text-parchment/60 mt-2">A taste of the riches waiting in the Bible Word Study.</p>
            </div>
            <Link to="/word-study" className="font-mono text-xs uppercase tracking-widest text-gold hover:text-parchment transition-colors inline-flex items-center gap-2">
              Study more <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {SPOTLIGHT.map((s) => (
              <div key={s.word} className="rounded-2xl border border-parchment/15 bg-parchment/5 p-7 hover:bg-parchment/10 transition-colors">
                <div className="flex items-baseline justify-between">
                  <span className="font-serif text-4xl text-gold">{s.script}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-parchment/50">{s.lang}</span>
                </div>
                <h3 className="font-serif text-2xl font-600 mt-4">{s.word}</h3>
                <p className="text-sm text-parchment/70 mt-2 leading-relaxed">{s.meaning}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick access */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CARDS.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              className="group bg-card rounded-2xl border border-border p-8 hover:border-gold hover:shadow-xl transition-all duration-300"
            >
              <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gold-light text-gold group-hover:bg-ink group-hover:text-gold transition-colors duration-300">
                <c.icon className="w-7 h-7" />
              </span>
              <h3 className="font-serif text-2xl font-600 text-ink mt-5">{c.title}</h3>
              <p className="text-sm text-ink/70 mt-2 leading-relaxed">{c.desc}</p>
              <span className="inline-flex items-center gap-2 mt-5 font-600 text-sm text-ink group-hover:text-gold transition-colors">
                Open <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Shift — purity CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-4">
        <div data-testid="shift-section" className="relative overflow-hidden rounded-3xl bg-ink-deep text-parchment p-8 sm:p-12">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold mb-4">
                <ShieldCheck className="w-4 h-4" /> Guard Your Heart & Eyes
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-600 leading-tight">
                Purity in a Distracted Age — Meet Shift
              </h2>
              <p className="mt-4 text-parchment/70 leading-relaxed max-w-2xl">
                "I will set no vile thing before my eyes." (Psalm 101:3) Shift is a
                free content-filtering tool that blocks pornography and explicit content across every
                app and browser on your phone — helping you walk in freedom and holiness.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <a
                href="https://shiftyourphone.com/content-filter/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shift-cta-link"
                className="inline-flex items-center gap-2 bg-gold hover:bg-parchment text-ink font-700 px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                <ShieldCheck className="w-5 h-5" /> Get Shift — Free
              </a>
              <p className="text-xs text-parchment/40 mt-3 font-mono">shiftyourphone.com · No cost, no catch</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
